/**
 * Client-side analytics via PostHog.
 *
 * Thin wrappers around posthog.capture() so call sites stay unchanged.
 * PostHog handles batching, retry, and session management internally.
 */

import posthog from "posthog-js";

/**
 * Derive a short source label from document.referrer.
 * Returns the first path segment of the referring page on the same origin,
 * or "direct" for external / missing referrers.
 */
function getSourcePage(): string {
  if (typeof window === "undefined") return "direct";
  try {
    const ref = document.referrer;
    if (!ref) return "direct";
    const url = new URL(ref);
    if (url.origin !== window.location.origin) return "external";
    const path = url.pathname;
    if (path === "/") return "home";
    // First segment: "/finance/..." → "finance"
    const segment = path.split("/").filter(Boolean)[0];
    return segment || "home";
  } catch {
    return "direct";
  }
}

export function trackPageView(page: string, slug?: string): void {
  if (typeof window === "undefined") return;

  if (page === "job_detail" && slug) {
    posthog.capture("job_detail_viewed", {
      job_slug: slug,
      source_page: getSourcePage(),
    });
  } else {
    posthog.capture("page_viewed", { page, slug });
  }
}

export function trackApplyClick(slug: string): void {
  if (typeof window === "undefined") return;
  posthog.capture("apply_clicked", { job_slug: slug });
}

export function trackSearch(query: string, tags: string[]): void {
  if (typeof window === "undefined") return;
  posthog.capture("search_filtered", { query, tags });
}

export function trackSponsoredJobClick(slug: string, position: number): void {
  if (typeof window === "undefined") return;
  posthog.capture("sponsored_job_clicked", { job_slug: slug, position });
}

export function trackSponsoredJobImpression(slugs: string[]): void {
  if (typeof window === "undefined") return;
  posthog.capture("sponsored_jobs_impressed", { job_slugs: slugs });
}

// --- Batched job impression tracking ---
const IMPRESSION_BATCH_SIZE = 20;
let impressionBuffer: string[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function flushImpressions(): void {
  if (impressionBuffer.length === 0) return;
  const slugs = [...impressionBuffer];
  impressionBuffer = [];
  posthog.capture("jobs_impressed", { job_slugs: slugs, count: slugs.length });
}

export function trackJobImpression(slug: string): void {
  if (typeof window === "undefined") return;
  impressionBuffer.push(slug);
  // Flush when batch is full (matches page size) or after 2s idle
  if (impressionBuffer.length >= IMPRESSION_BATCH_SIZE) {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = null;
    flushImpressions();
  } else {
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flushImpressions, 2000);
  }
}
